// apps/web/src/features/readingnote/pages/BookReviewNoteListPage.tsx
import React, { useEffect, useMemo, useState, useLayoutEffect, useCallback } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { HEADER, withBase } from "@/ui/Header";
import { me, reviewsByBook, reviewToggleLike, reviewDelete } from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { customConfirm } from "@/ui/confirm";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";
import { SPC_MODEL, getDeviceModel } from "@/lib/androidBridge";

/* ───────── Layout / Scale ───────── */
const BASE = {
    W: 1264,
    MARGIN_X: 108,
    TAB_H: 78,
    TAB_FZ: 35,
    PAGER_H: 20,

    LIST_ROWS: 4,
    ROW_H: 250,
    ROW_GAP: 12,

    NAME_FZ: 28,
    DATE_FZ: 18,
    BODY_FZ: 28,

    WRITE_ICON: 100,
} as const;

function useScale() {
    // 원래 max 1로 제한하는 패턴이었으니 여기도 max: 1 적용
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1 });
    return { s, px, sf, sfpx };
}

const ICONS = {
    back: withBase("icons/prev1.svg"),
    first: withBase("icons/prev2.svg"),
    prev: withBase("icons/prev1.svg"),
    next: withBase("icons/next1.svg"),
    last: withBase("icons/next2.svg"),
    write: withBase("icons/review_write.svg"),
};

function StarIcon({ filled, size }: { filled: boolean; size: number }) {
    const fill = filled ? "#111" : "#D1D5DB";
    const stroke = filled ? "#111" : "#D1D5DB";

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
            <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={fill}
                stroke={stroke}
                strokeWidth={2.6}
                strokeLinejoin="round"
            />
        </svg>
    );
}

/* ───────── Small Utilities ───────── */
function formatDate(v?: unknown): string {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(String(v));
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${dd}`;
}

function useOverflow(ref: React.RefObject<HTMLElement>, content: string, fontPx: number) {
    const [overflowed, setOverflowed] = useState(false);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const measurer = document.createElement("div");
        Object.assign(measurer.style, {
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            left: "-99999px",
            top: "-99999px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflow: "visible",
        } as CSSStyleDeclaration);

        document.body.appendChild(measurer);

        const compute = () => {
            const width = el.clientWidth || el.getBoundingClientRect().width;
            measurer.style.width = `${width}px`;
            measurer.style.fontSize = `${fontPx}px`;
            measurer.style.lineHeight = "1.6";
            measurer.textContent = content || "";
            setOverflowed(measurer.scrollHeight > el.clientHeight + 1);
        };

        compute();
        const t1 = setTimeout(compute, 0);
        const t2 = setTimeout(compute, 80);
        const t3 = setTimeout(compute, 160);

        const RO: any = (window as any).ResizeObserver;
        let ro: any = null;
        if (RO) {
            ro = new RO(compute);
            ro.observe(el);
        }
        window.addEventListener("resize", compute);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            if (ro) ro.disconnect();
            window.removeEventListener("resize", compute);
            document.body.removeChild(measurer);
        };
    }, [ref, content, fontPx]);

    return overflowed;
}

/* ───────── UI Bits ───────── */
function SortTabs({
                      value,
                      onChange,
                      px,
                      s,
                      sfpx,
                  }: {
    value: "latest" | "popular";
    onChange: (v: "latest" | "popular") => void;
    px: (n: number) => string;
    s: (n: number) => number;
    sfpx: (n: number) => string;
}) {
    const Tab = ({ v, label }: { v: "latest" | "popular"; label: string }) => {
        const active = value === v;
        return (
            <button
                onClick={() => onChange(v)}
                style={{
                    height: px(BASE.TAB_H),
                    width: "100%",
                    fontSize: sfpx(BASE.TAB_FZ),
                    fontWeight: FW.extraBold,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: COLOR.text,
                    borderBottom: active ? `${s(4)}px solid #000` : `${s(2)}px solid #e5e7eb`,
                }}
            >
                {label}
            </button>
        );
    };
    return (
        <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <Tab v="latest" label="최신순" />
            <Tab v="popular" label="인기순" />
        </div>
    );
}

function Pager({
                   page,
                   totalPages,
                   hasNext,
                   onFirst,
                   onPrev,
                   onNext,
                   onLast,
                   px,
                   sfpx,
               }: {
    page: number;
    totalPages: number;
    hasNext: boolean;
    onFirst: () => void;
    onPrev: () => void;
    onNext: () => void;
    onLast: () => void;
    px: (n: number) => string;
    sfpx: (n: number) => string;
}) {
    return (
        <div style={{ width: "100%", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <button
                    disabled={!(page > 1)}
                    onClick={onFirst}
                    aria-label="처음"
                    style={{ lineHeight: 0, background: "transparent", marginRight: px(48), border: "none", opacity: page > 1 ? 1 : 0.4, cursor: "pointer" }}
                >
                    <img src={ICONS.first} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <button
                    disabled={!(page > 1)}
                    onClick={onPrev}
                    aria-label="이전"
                    style={{ lineHeight: 0, background: "transparent", marginRight: px(46), border: "none", opacity: page > 1 ? 1 : 0.4, cursor: "pointer" }}
                >
                    <img src={ICONS.prev} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <span style={{ fontWeight: 700, fontFeatureSettings: "'tnum' 1", fontSize: sfpx(34), minWidth: px(120), textAlign: "center", marginRight: px(46) }}>
          {Math.max(1, page)} / {Math.max(1, totalPages)}
        </span>
                <button
                    disabled={!hasNext}
                    onClick={onNext}
                    aria-label="다음"
                    style={{ lineHeight: 0, background: "transparent", marginRight: px(48), border: "none", opacity: hasNext ? 1 : 0.4, cursor: "pointer" }}
                >
                    <img src={ICONS.next} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <button
                    disabled={!hasNext}
                    onClick={onLast}
                    aria-label="마지막"
                    style={{ lineHeight: 0, background: "transparent", border: "none", opacity: hasNext ? 1 : 0.4, cursor: "pointer" }}
                >
                    <img src={ICONS.last} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
            </div>
        </div>
    );
}

/* ───────── Review Item ───────── */
export type ReviewItemData = {
    id: number | string;
    user_id: number | string;
    book_id: number | string;
    content: string;
    rating?: number | null;
    keywords?: string[];
    likes_count?: number;
    author?: { id?: number | string; name?: string; avatar_url?: string | null };
    liked_by_me?: boolean;
    is_owner?: boolean;
    created_at?: string; // server normalized
    updated_at?: string; // server normalized
    // fallback keys if server ever sends camelCase
    createdAt?: string;
    updatedAt?: string;
};

function ReviewItem({
                        it,
                        px,
                        s,
                        sfpx,
                        rowH,
                        onLike,
                        onEdit,
                        onDelete,
                        onMore,
                    }: {
    it: ReviewItemData;
    px: (n: number) => string;
    s: (n: number) => number;
    sfpx: (n: number) => string;
    rowH: number;
    onLike: (id: number) => void;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    onMore: (item: ReviewItemData) => void;
}) {
    const content = String(it.content || "");
    const dateRaw = it.updated_at || it.created_at || it.updatedAt || it.createdAt;
    const dateLabel = formatDate(dateRaw);

    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const isOverflow = useOverflow(contentRef, content, s(BASE.BODY_FZ));

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "1fr 164px",
                gridTemplateRows: "auto 1fr auto",
                gap: px(12),
                borderBottom: BORDER.muted,
                padding: `${px(12)} 0`,
                height: `${rowH}px`,
                overflow: "hidden",
                position: "relative",
            }}
        >
            {/* 작성자 + 날짜 */}
            <div
                style={{
                    gridColumn: "1 / 2",
                    gridRow: "1 / 2",
                    display: "flex",
                    alignItems: "baseline",
                    gap: px(12),
                    overflow: "hidden",
                }}
            >
                <span style={{
                    fontWeight: FW.extraBold,
                    fontSize: sfpx(BASE.NAME_FZ),
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flexShrink: 1,
                }} title={it.author?.name || "익명"}>
                    {it.author?.name || "익명"}
                </span>
                <span style={{ fontSize: sfpx(20), color: "#999", lineHeight: 1.2, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {dateLabel}
                </span>
            </div>

            {/* 우측 액션 (상단에 고정) */}
            <div style={{ gridColumn: "2 / 3", gridRow: "1 / 3", position: "relative", minHeight: 0 }}>
                <div style={{ position: "absolute", top: 0, right: 0, display: "grid", rowGap: px(8), justifyItems: "end" }}>
                    {it.is_owner ? (
                        <>
                            <button
                                onClick={() => onEdit(Number(it.id))}
                                style={{ border: BORDER.darkText, background: COLOR.white, borderRadius: 10, padding: `${px(8)} ${px(12)}`, fontSize: sfpx(22), fontWeight: FW.extraBold, cursor: "pointer" }}
                            >
                                수정
                            </button>
                            <button
                                onClick={() => onDelete(Number(it.id))}
                                style={{ border: `1px solid ${COLOR.error}`, color: COLOR.error, background: COLOR.white, borderRadius: 10, padding: `${px(8)} ${px(12)}`, fontSize: sfpx(22), fontWeight: FW.extraBold, cursor: "pointer" }}
                            >
                                삭제
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => onLike(Number(it.id))}
                            style={{ border: BORDER.light, background: COLOR.white, borderRadius: 10, padding: `${px(8)} ${px(12)}`, fontSize: sfpx(22), cursor: "pointer" }}
                        >
                            {it.liked_by_me ? "좋아요 취소" : "좋아요"}
                        </button>
                    )}
                </div>
            </div>

            {/* 내용 (2줄 클램프) */}
            <div
                ref={contentRef}
                style={{
                    gridColumn: "1 / 2",
                    gridRow: "2 / 3",
                    color: COLOR.text,
                    fontSize: sfpx(BASE.BODY_FZ),
                    lineHeight: 1.6,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical" as any,
                    WebkitLineClamp: 2 as any,
                    overflow: "hidden",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {content}
            </div>

            {/* 푸터: 별(한 줄) -> 키워드(다음 줄) + 우측(좋아요/더보기는 별 줄 오른쪽) */}
            <div
                style={{
                    gridColumn: "1 / 3",
                    gridRow: "3 / 4",
                    display: "flex",
                    flexDirection: "column",
                    gap: px(10),
                    minHeight: px(32),
                }}
            >
                {/* 1) 별 줄 + 좋아요/더보기 */}
                <div style={{ display: "flex", alignItems: "center", gap: px(10) }}>
                    {/* 별 5개: 선택된 개수만 검정, 나머지는 회색 채움 */}
                    {(() => {
                        const r = Math.max(0, Math.min(5, Number(it.rating || 0)));
                        if (r <= 0) return null;

                        const starSize = Math.round(s(32)); // ✅ 크기 조절 포인트(원하면 36~40)
                        return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: px(6), flexShrink: 0 }}>
                        {[1, 2, 3, 4, 5].map((n) => (
                            <StarIcon key={n} filled={n <= r} size={starSize} />
                        ))}
                            </span>
                        );
                    })()}

                    <div style={{ flex: 1 }} />

                    <span style={{ fontSize: sfpx(20), color: "#999", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                        좋아요 {it.likes_count || 0}
                    </span>

                    {isOverflow && (
                        <button
                            onClick={() => onMore(it)}
                            style={{
                                marginLeft: px(10),
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                color: "#666",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontSize: sfpx(20),
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                            }}
                            aria-label="더보기"
                            title="전체 보기"
                        >
                            더보기
                        </button>
                    )}
                </div>

                {/* 2) 키워드 줄 (별 아래로 내려감) */}
                {(() => {
                    const kw = it.keywords || [];
                    if (kw.length === 0) return null;
                    return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: px(8), alignItems: "center" }}>
                            {kw.map((k, i) => (
                                <span
                                    key={i}
                                    style={{
                                        display: "inline-block",
                                        padding: `${px(6)} ${px(14)}`,
                                        fontSize: sfpx(22),
                                        fontWeight: FW.medium,
                                        borderRadius: RADIUS.full,
                                        border: BORDER.dark,
                                        background: COLOR.white,
                                        color: COLOR.black,
                                        whiteSpace: "nowrap",
                                        lineHeight: 1.4,
                                    }}
                                >{k}
                                </span>
                            ))}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
}

/* ───────── 전체 보기 모달 ───────── */
function FullReviewModal({ open, item, onClose, px, sfpx }: { open: boolean; item: ReviewItemData | null; onClose: () => void; px: (n: number) => string; sfpx: (n: number) => string }) {
    if (!open || !item) return null;
    const dateRaw = item.updated_at || item.created_at || item.updatedAt || item.createdAt;
    return (
        <div
            onClick={onClose}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(1048px, 94vw)",
                    maxHeight: "90vh",
                    minHeight: "360px",
                    background: COLOR.white,
                    borderRadius: RADIUS.lg,
                    padding: 16,
                    boxShadow: "0 10px 30px rgba(0,0,0,.15)",
                    display: "grid",
                    gridTemplateRows: "auto minmax(0,1fr) auto",
                    rowGap: 12,
                }}
            >
                <div>
                    <div style={{ fontWeight: FW.extraBold, fontSize: sfpx(28) }}>{item.author?.name || "익명"}</div>
                    <div style={{ color: "#999", fontSize: sfpx(18), marginTop: px(4) }}>{formatDate(dateRaw)}</div>
                </div>

                <div style={{ minHeight: 0, overflowY: "auto", paddingRight: 8, color: COLOR.text, fontSize: sfpx(24), lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                    {String(item.content || "")}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button onClick={onClose} style={{ height: 44, borderRadius: 10, border: "1px solid #ddd", background: COLOR.white, padding: `0 ${px(16)}` }}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ───────── Page ───────── */
export default function BookReviewNoteListPage() {
    const { px, s, sf, sfpx } = useScale();
    const nav = useNavigate();
    const { bookId } = useParams();
    const bid = Number(bookId);

    const [hasMyReview, setHasMyReview] = useState(false);
    const [sort, setSort] = useState<"latest" | "popular">("latest");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [items, setItems] = useState<ReviewItemData[]>([]);
    const pageSize = BASE.LIST_ROWS; // 4개 고정

    const [openFull, setOpenFull] = useState(false);
    const [fullItem, setFullItem] = useState<ReviewItemData | null>(null);
    const openMore = useCallback((it: ReviewItemData) => {
        setFullItem(it);
        setOpenFull(true);
    }, []);
    const closeMore = useCallback(() => {
        setOpenFull(false);
        setFullItem(null);
    }, []);

    useEffect(() => {
        setPage(1);
    }, [sort]);

    useEffect(() => {
        (async () => {
            try {
                await me();
            } catch (e: any) {
                // 401 에러일 때만 로그인 페이지로 이동
                if (e?.status === 401) {
                    nav(`/auth/login?next=${encodeURIComponent(location.pathname)}`);
                    return;
                }
                // 다른 에러는 무시하고 계속 진행
            }
            try {
                const r = await reviewsByBook(bid, { sort, page, size: pageSize });
                setItems((r as any).items || []);
                setTotal((r as any).total || 0);
                setHasMyReview(!!(((r as any).my_review) || (((r as any).items || []).some((x: any) => x.is_owner))));
            } catch {
                // 리뷰 목록 로드 실패 시 빈 상태로 표시 (로그인 리다이렉트 안함)
            }
        })();
    }, [bid, sort, page, pageSize, nav]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const hasNext = page < totalPages;

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({
            next: () => setPage((p) => Math.min(totalPages, p + 1)),
            prev: () => setPage((p) => Math.max(1, p - 1)),
            first: () => setPage(1),
            last: () => setPage(totalPages),
        });
        return () => unregisterPager();
    }, [totalPages]);

    const like = useCallback(async (id: number) => {
        const r = await reviewToggleLike(id);
        setItems((x) => x.map((it) => (Number(it.id) === id ? { ...it, liked_by_me: (r as any).liked, likes_count: (r as any).likes_count } : it)));
    }, []);

    const del = useCallback(async (id: number) => {
        const ok = await customConfirm("리뷰를 삭제할까요?");
        if (!ok) return;
        await reviewDelete(id);
        const r = await reviewsByBook(bid, { sort, page, size: pageSize });
        setItems((r as any).items || []);
        setTotal((r as any).total || 0);
    }, [bid, sort, page, pageSize]);

    const isSpc = getDeviceModel() === SPC_MODEL;
    const { rowH, pagerH } = useMemo(() => {
        if (isSpc) return { rowH: s(450), pagerH: s(140) };
        return { rowH: s(320), pagerH: s(100) };
    }, [s, isSpc]);

    const listH = pageSize * rowH + (pageSize - 1) * s(BASE.ROW_GAP);

    return (
        <PageShell title="리뷰 보기" onBack={() => nav(-1)} backIcon={ICONS.back} headerHeight={HEADER.H} width={px(BASE.W)}>
            <div style={{ gridArea: "body", display: "flex", flexDirection: "column", height: "100%" }}>
                <SortTabs value={sort} onChange={setSort} px={px} s={s} sfpx={sfpx} />

                {/* 콘텐츠 — takes remaining space */}
                <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <div style={{ width: px(BASE.W), margin: "0 auto", height: "100%" }}>
                        <div style={{ marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X), paddingTop: px(24) }}>
                            <div style={{ height: `${listH}px`, overflow: "hidden" }}>
                                {items.length === 0 && <div style={{ padding: px(24), color: "#666" }}>아직 리뷰가 없습니다.</div>}

                                <div style={{ display: "grid", rowGap: px(BASE.ROW_GAP) }}>
                                    {items.map((it) => (
                                        <ReviewItem
                                            key={String(it.id)}
                                            it={it}
                                            px={px}
                                            s={s}
                                            sfpx={sfpx}
                                            rowH={rowH}
                                            onLike={like}
                                            onEdit={(id) => nav(`/reviews/${id}/edit`)}
                                            onDelete={del}
                                            onMore={openMore}
                                        />
                                    ))}
                                    {items.length < BASE.LIST_ROWS &&
                                        Array.from({ length: BASE.LIST_ROWS - items.length }).map((_, i) => (
                                            <div key={`empty-${i}`} style={{ height: `${rowH}px`, borderBottom: BORDER.muted }} />
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 페이저 — pinned to bottom */}
                <div style={{ flexShrink: 0, height: `${pagerH}px`, marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X) }}>
                    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", height: "100%", columnGap: px(12) }}>
                        {/* 좌측: 리뷰 쓰기 */}
                        {!hasMyReview ? (
                            <button
                                onClick={() => nav(`/reviews/new?bookId=${bid}`)}
                                aria-label="리뷰 쓰기"
                                style={{
                                    width: px(BASE.WRITE_ICON),
                                    height: px(BASE.WRITE_ICON),
                                    lineHeight: 0,
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "grid",
                                    placeItems: "center",
                                }}
                            >
                                <img src={ICONS.write} alt="" style={{ width: "100%", height: "100%", display: "block" }} />
                            </button>
                        ) : (
                            <div style={{ width: px(BASE.WRITE_ICON), height: px(BASE.WRITE_ICON) }} />
                        )}

                        {/* 가운데: 페이저 */}
                        <Pager
                            page={page}
                            totalPages={totalPages}
                            hasNext={hasNext}
                            onFirst={() => setPage(1)}
                            onPrev={() => setPage((p) => Math.max(1, p - 1))}
                            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                            onLast={() => setPage(totalPages)}
                            px={px}
                            sfpx={sfpx}
                        />

                        {/* 우측: 비움(중앙정렬 유지용) */}
                        <div style={{ width: px(BASE.WRITE_ICON), height: px(BASE.WRITE_ICON) }} />
                    </div>
                </div>
            </div>

            {/* 전체보기 모달 */}
            <FullReviewModal open={openFull} item={fullItem} onClose={closeMore} px={px} sfpx={sfpx} />
        </PageShell>
    );
}
